/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import { Fragment, useState } from 'react';
import { Button } from '@/components/Button';
import { Label } from '@/features/i18n';
import { useLocale } from '@/hooks/useLocale';
import { SmartLink as Link } from '@/utils/smartLink';
import { routePaths } from '@/constants/routePaths';
import { openChatWindow } from '@/features/proactiveChat';
import { trackChatLink } from '@/utils/analytics/track/trackChatLink';
import { YMMEModal } from '@/features/ymme/components/YMMEModal';
import { pageTypes } from '@/constants/page';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { sessionStorage } from '@/utils/sessionStorage';
import styles from './styles.module.scss';
import { countryCodes } from '@/constants/locale';
import { useAppData } from '@/hooks/useAppData';
import cx from 'classnames';

const labelMap = {
  lblNoResults: 'label_no_results_found',
  lblTryAgain: 'label_Please_try_another_search',
  lblSearchTips: 'label_Search_NullResults_SearchTips',
  lblSpelling: 'label_Search_NullResults_DoubleCheckYourSpelling',
  lblSingleWords: 'label_Search_NullResults_TryUsingSingleWords',
  lblLessSpecific: 'label_Search_NullResults_TrySearchingForAnItemThatIsLessSpecific',
  lblNeedHelp: 'label_Need_Help?',
  lblContactUs: 'label_Contact_Us',
  lblChatNow: 'label_Chat_Now',
  lblOr: 'label_or',
  lblOpenaNewWindow: 'label_OpenaNewWindow',
  lblForYour: 'label_null_search_For_your',
  lblExpandResults: 'label_expand_results',
  lblTryExpandingResults: 'label_try_expanding_results',
};

export const SearchNoResults = ({
  queryDisplayLabel,
  vehicleName,
  showExpandResults,
  isFullWidth,
}: {
  queryDisplayLabel?: string;
  vehicleName?: string;
  showExpandResults?: boolean;
  isFullWidth?: boolean;
}) => {
  const { appData } = useAppData();
  const { isChatAgentAvailable } = appData;
  const locale = useLocale();
  const isBR = locale === countryCodes.ptBr;

  const [showYmme, setShowYmme] = useState(false);
  const legacyRouter = useLegacyRouter();

  const handleExpandResults = () => {
    if (!legacyRouter.push) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { loadMore: _loadMore, ...currentQuery } = legacyRouter.query;
    sessionStorage.setItem('expandResultsAnalytics', 'true');
    void legacyRouter.push({
      query: { ...currentQuery, ignoreVehicleSpecificProductsCheck: 'true' },
    });
  };

  const showSearchLiveChat = () => {
    openChatWindow();
    /* Analytics start */

    if (window.utag_data?.pageName) {
      trackChatLink(window.utag_data.pageName);
    }
    /* Analytics end */
  };
  return (
    <div className={cx(styles.noResultsContainer, { [styles.fullWidth]: isFullWidth })}>
      <div className={styles.noResultsTryAnother}>
        <span className={styles.noResultsHeader}>
          <Label label={labelMap.lblNoResults} />{' '}
          <strong>
            {'"'}
            {queryDisplayLabel}
            {'"'}
          </strong>
        </span>
        {vehicleName && (
          <span className={styles.noResultsSubHeader}>
            <Label label={labelMap.lblForYour} />{' '}
            <button
              type="button"
              className={styles.vehicleSelector}
              onClick={() => setShowYmme(true)}
            >
              {vehicleName}
            </button>
          </span>
        )}
        {showExpandResults && (
          <>
            <div className={styles.universalSubheader}>
              <Label label={labelMap.lblTryExpandingResults} />
            </div>
            <div className={styles.expandResults}>
              <Button fullWidth={true} variant="contained" onClick={handleExpandResults}>
                <Label label={labelMap.lblExpandResults} />
              </Button>
            </div>
          </>
        )}
        <YMMEModal
          showYmme={showYmme}
          closeYmmeWidget={() => setShowYmme(false)}
          pageType={pageTypes.search}
        />
      </div>
      <div className={styles.searchTips}>
        <div className={styles.searchTipsHeader}>
          <Label label={labelMap.lblSearchTips} />:
        </div>
        <ul className={styles.searchTipsList}>
          <li>
            <Label label={labelMap.lblSpelling} />
          </li>
          <li>
            <Label label={labelMap.lblLessSpecific} />
          </li>
          <li>
            <Label label={labelMap.lblSingleWords} />
          </li>
          {!isBR ? (
            <li>
              <Label label={labelMap.lblNeedHelp} />{' '}
              <Link to={routePaths.contactUs} className={styles.needHelpContactUs}>
                <Label label={labelMap.lblContactUs} />
              </Link>{' '}
              {isChatAgentAvailable ? (
                <Fragment>
                  <Label label={labelMap.lblOr} />{' '}
                  <Button
                    id="searchliveChat"
                    variant="link"
                    data-testid="searchliveChat"
                    customClass={styles.needHelpChat}
                    onClick={showSearchLiveChat}
                  >
                    <span>
                      <Label label={labelMap.lblChatNow} />
                    </span>
                    <span className={styles.sr}>
                      <Label label={labelMap.lblOpenaNewWindow} />
                    </span>
                  </Button>
                </Fragment>
              ) : null}
            </li>
          ) : null}
        </ul>
      </div>
    </div>
  );
};
